import {
    Grid,
    Typography,
    useTheme,
    Box,
    useMediaQuery,    
    Paper,
    LinearProgress,
    Stack,

} from "@mui/material";
import React from "react";
import { SchoolAggregateData } from "types/School";



const SchoolDashboardSummary = ({
    schoolStrength,
    testTaken,
    numOfClasses,
    overallPerformance,
    identification,
    memory,
    numericalSkills,
    reasoning,
    visualDiscrimination,
    auditoryDiscrimination,
}: SchoolAggregateData) => {
    const { palette } = useTheme();
    if (
        !overallPerformance ||
        !identification ||
        !memory ||
        !numericalSkills ||
        !reasoning ||
        !visualDiscrimination ||
        !auditoryDiscrimination
    ) {
        return <Typography variant="h2">No data available</Typography>;
    }
    
        const resultValues = [{ category: "Identification", score: identification.valueOf() },
            { category: "Memory", score: memory.valueOf() },
            { category: "Numerical Skills", score: numericalSkills.valueOf() },
            { category: "Reasoning", score: reasoning.valueOf() },
            { category: "Visual Discrimination", score: visualDiscrimination.valueOf() },
            { category: "Auditory Discrimination", score: auditoryDiscrimination.valueOf() }];

        const sortedResultValues = [...resultValues]
        .sort((a, b) => b.score - a.score);
            
    

    const ProgressWithLabel: React.FC<{ label: string; value: number; color: string }> = ({ label, value, color }) => (
        <Box mb={2}>
            <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">{label}</Typography>
                <Typography variant="body2" color="text.secondary">
                    {value}%
                </Typography>
            </Stack>
            <LinearProgress
                variant="determinate"
                value={value}
                sx={{
                    height: 8,
                    borderRadius: 5,
                    [`& .MuiLinearProgress-bar`]: {
                        backgroundColor: color,
                    },
                    backgroundColor: '#f0f0f0',
                }}
            />
        </Box>
    );

   
    return (
        <>
            {/* Top Metrics */}
            <Grid container spacing={2} mb={4} border="1" marginBottom={2}>
                {[
                    { label: 'School Strength', value: schoolStrength },
                    { label: 'Tests Taken', value: testTaken },
                    { label: 'Classes Tested', value: numOfClasses },

                ].map((item) => (
                    <Grid item xs={12} sm={5} key={item.label}>
                        <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                {item.label}
                            </Typography>
                            <Typography variant="h5" fontWeight="bold">
                                {item.value}
                            </Typography>
                        </Paper>
                    </Grid>
                ))
                }
                < Grid item xs={12} sm={5}>
                    <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            Overall Performance
                        </Typography>
                        <Typography variant="h5" fontWeight="bold">
                            {overallPerformance}%
                        </Typography>
                    </Paper>



                </Grid>
            </Grid>

            <Grid item xs={12} md={6} marginBottom={2}>
                <Paper elevation={1} sx={{ p: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        At a Glance
                    </Typography>
                    <Typography variant="body2">
                        Most students demonstrated strong <strong>{sortedResultValues[0].category}</strong> and{' '}
                        <strong>{sortedResultValues[1].category}</strong> skills, with {sortedResultValues[5].category} as an area to support.
                    </Typography>
                </Paper>
            </Grid>
           
            {/* Cognitive Ability Averages */}
            <Paper elevation={1} sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Cognitive Ability Averages
                </Typography>
                <ProgressWithLabel label="Identification" value={(identification ? identification : 0)} color="#f44336" />
                <ProgressWithLabel label="Memory" value={memory ? memory : 0} color="#673ab7" />
                <ProgressWithLabel label="Numerical Skills" value={(numericalSkills ? numericalSkills : 0)} color="#2196f3" />
                <ProgressWithLabel label="Reasoning" value={(reasoning ? reasoning : 0)} color="#ffeb3b" />
                <ProgressWithLabel label="Visual Discrimination" value={(visualDiscrimination ? visualDiscrimination : 0)} color="#4caf50" />
                <ProgressWithLabel label="Auditory Discrimination" value={(auditoryDiscrimination ? auditoryDiscrimination : 0)} color="#800080" />
            </Paper>

        </>
    );
};

export default SchoolDashboardSummary;
